import { Alert, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { selectedPlayerNameAtom } from '@/atoms/player/info';
import { playerComparisonDisplayNameAtom } from '@/atoms/queries/player/playerComparisonInfo';
import { playerComparisonMinutesPlayedAtom } from '@/atoms/queries/player/playerComparisonRadar';
import { playerAggsMinutesPlayedAtom } from '@/atoms/queries/player/playerMinsPlayed';
import { LOW_MINUTES_THRESHOLD } from '@/consts/filters/ranges';
import { useIsComparisonActive } from '@/hooks/useIsComparisonActive';
import { useIsCompetitionAverageMode } from '@/hooks/useIsCompetitionAverageMode';
import { isNullish } from '@/utils/predicates';

const isLow = (minutes: number | undefined) => !isNullish(minutes) && minutes < LOW_MINUTES_THRESHOLD;

const RadarLowMinutesWarningWithData = () => {
  const { t } = useTranslation('dataVis');
  const primaryMinutes = useAtomValue(playerAggsMinutesPlayedAtom);
  const comparisonMinutes = useAtomValue(playerComparisonMinutesPlayedAtom);
  const primaryName = useAtomValue(selectedPlayerNameAtom);
  const comparisonName = useAtomValue(playerComparisonDisplayNameAtom);
  const isComparisonActive = useIsComparisonActive();
  const isCompetitionAverageMode = useIsCompetitionAverageMode();

  const isPrimaryLow = isLow(primaryMinutes);
  const isComparisonLow = isComparisonActive && !isCompetitionAverageMode && isLow(comparisonMinutes);

  if (!isPrimaryLow && !isComparisonLow) return null;

  const message =
    isPrimaryLow && isComparisonLow ? (
      <Trans
        t={t}
        i18nKey="lowMinutesWarningBoth"
        values={{ primaryName, comparisonName }}
        components={{ bold: <strong /> }}
      />
    ) : (
      <Trans
        t={t}
        i18nKey="lowMinutesWarning"
        values={{ name: isPrimaryLow ? primaryName : comparisonName }}
        components={{ bold: <strong /> }}
      />
    );

  return (
    <Alert type="warning">
      <Typography variant="bodyMedium" muted>
        {message}
      </Typography>
    </Alert>
  );
};

export const RadarLowMinutesWarning = () => (
  <Suspense>
    <RadarLowMinutesWarningWithData />
  </Suspense>
);
